export interface DatasourceOption {
  dataset_name: string;
  table_name: string;
  table_description: string;
}

export type ColumnAggregation = 'sum' | 'average' | 'none' | 'disabled';

export interface DatasetColumn {
  key: string;
  name: string;
  alias: string;
  type: 'Text' | 'Number';
  description: string;
  data_type: string;
  is_primary_key: boolean;
  is_foreign_key: boolean;
  sample_values: string;
  synonyms: string | null;
  table_name: string;
  selected: boolean;
  aggregation: ColumnAggregation;
}

export interface DatasetConfig {
  id: string;
  name: string;
  dataset_name: string;
  table_name: string;
  table_description: string;
  isPrimary: boolean;
  manageColumnsOpen: boolean;
  columnsLoading: boolean;
  columnsLoaded: boolean;
  columns: DatasetColumn[];
  relationshipsOpen: boolean;
  joins: DatasetJoin[];
}

export type JoinType = 'INNER JOIN' | 'LEFT JOIN' | 'RIGHT JOIN' | 'FULL OUTER JOIN' | 'CROSS JOIN';

export interface JoinTypeOption {
  value: JoinType;
  label: string;
}

export const JOIN_TYPE_OPTIONS: JoinTypeOption[] = [
  { value: 'INNER JOIN', label: 'Keep only matching records from both datasets' },
  { value: 'LEFT JOIN', label: 'Keep all rows from Dataset 1' },
  { value: 'RIGHT JOIN', label: 'Keep all rows from Dataset 2' },
  { value: 'FULL OUTER JOIN', label: 'Keep all records from both datasets' },
  { value: 'CROSS JOIN', label: 'Create all possible combinations of records' }
];

export interface ColumnMapping {
  id: string;
  leftColumn: string | null;
  rightColumn: string | null;
  // UI-only search terms for the column dropdowns, not sent in the save payload.
  leftColumnSearch?: string;
  rightColumnSearch?: string;
}

export interface DatasetJoin {
  id: string;
  targetDatasetId: string | null;
  joinType: JoinType | null;
  columnMappings: ColumnMapping[];
}

export interface SelectDatasourceSubmitPayload {
  datasets: Array<{
    id: string;
    name: string;
    table_name: string;
    table_description: string;
    isPrimary: boolean;
    columns: Array<Pick<DatasetColumn, 'key' | 'name' | 'type' | 'aggregation'>>;
    joins: Array<{
      targetDatasetId: string | null;
      joinType: JoinType | null;
      columnMappings: Array<{ leftColumn: string | null; rightColumn: string | null }>;
    }>;
  }>;
}

const NUMERIC_DATA_TYPES = ['INT', 'INTEGER', 'BIGINT', 'SMALLINT', 'TINYINT', 'DECIMAL', 'NUMERIC', 'FLOAT', 'DOUBLE', 'REAL'];

export function isNumericDataType(dataType: string | null | undefined): boolean {
  return NUMERIC_DATA_TYPES.includes((dataType || '').toUpperCase());
}

// Maps a raw manage-columns API record into the shape used by the UI/payload.
export function mapApiColumn(raw: any): DatasetColumn {
  const isNumber = isNumericDataType(raw?.data_type);
  return {
    key: raw?.column_name,
    name: raw?.column_name,
    alias: raw?.alias,
    type: isNumber ? 'Number' : 'Text',
    description: raw?.column_description,
    data_type: raw?.data_type,
    is_primary_key: !!raw?.is_primary_key,
    is_foreign_key: !!raw?.is_foreign_key,
    sample_values: raw?.sample_values,
    synonyms: raw?.synonyms ?? null,
    table_name: raw?.table_name,
    selected: false,
    aggregation: isNumber ? 'none' : 'disabled'
  };
}

// LEFT/RIGHT JOIN keep "Dataset 1"/"Dataset 2" wording from JOIN_TYPE_OPTIONS but swap in the actual table names.
export function buildJoinTypeOptions(targetDatasetName: string, currentDatasetName: string): JoinTypeOption[] {
  const target = targetDatasetName || 'Dataset 1';
  const current = currentDatasetName || 'Dataset 2';
  return JOIN_TYPE_OPTIONS.map(option => {
    if (option.value === 'LEFT JOIN') {
      return { ...option, label: `Keep all rows from ${target}` };
    }
    if (option.value === 'RIGHT JOIN') {
      return { ...option, label: `Keep all rows from ${current}` };
    }
    return option;
  });
}

function normalizeColumnToken(value: string | null | undefined): string {
  return (value || '').toString().trim().toLowerCase().replace(/[^a-z0-9]/g, '');
}

// Two columns can be mapped together if they share a name/alias/synonym once punctuation and casing
// are stripped (e.g. "ASK_GLOBAL_ID" and "ask global id" both normalize to "askglobalid").
export function areColumnsSynonymous(left: DatasetColumn | undefined, right: DatasetColumn | undefined): boolean {
  if (!left || !right) {
    return false;
  }
  const leftTokens = new Set(
    [left.name, left.alias, ...((left.synonyms || '').split(','))].map(normalizeColumnToken).filter(Boolean)
  );
  const rightTokens = new Set(
    [right.name, right.alias, ...((right.synonyms || '').split(','))].map(normalizeColumnToken).filter(Boolean)
  );
  for (const token of leftTokens) {
    if (rightTokens.has(token)) {
      return true;
    }
  }
  return false;
}

export function findDatasetColumn(dataset: DatasetConfig | undefined, key: string | null): DatasetColumn | undefined {
  if (!dataset || !key) {
    return undefined;
  }
  return dataset.columns.find(column => column.key === key);
}

export function isColumnMappingValid(
  mapping: ColumnMapping,
  targetDataset: DatasetConfig | undefined,
  ownDataset: DatasetConfig | undefined
): boolean {
  if (!mapping.leftColumn || !mapping.rightColumn) {
    return false;
  }
  return areColumnsSynonymous(
    findDatasetColumn(targetDataset, mapping.leftColumn),
    findDatasetColumn(ownDataset, mapping.rightColumn)
  );
}

// A join is fully configured once a target + join type are chosen and at least one valid (synonymous) column mapping exists.
export function isJoinFullyConfigured(join: DatasetJoin, ownDataset: DatasetConfig, allDatasets: DatasetConfig[]): boolean {
  if (!join.targetDatasetId || !join.joinType) {
    return false;
  }
  const target = allDatasets.find(dataset => dataset.id === join.targetDatasetId);
  return join.columnMappings.some(mapping => isColumnMappingValid(mapping, target, ownDataset));
}
